import { type RecommendationsTabTooltipWrapperMutation } from '#app/__generated__/relay/RecommendationsTabTooltipWrapperMutation.graphql.js';
import { type UserInterfaceFlag } from '#app/__generated__/relay/useUserInterfaceFlags_fragment.graphql.js';
import { OnboardingTooltip } from '#app/components/OnboardingTooltip/OnboardingTooltip.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import {
  type ContraMutationErrors,
  useContraMutation,
} from '#app/hooks/useContraMutation.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import {
  HIDE_CLIENT_FIRST_RECOMMENDATION_TOOLTIP,
  HIDE_CONTRACTOR_FIRST_RECOMMENDATION_TOOLTIP,
} from './constants.js';
import { ConfettiEmoji } from '@contra/icons/emojis';
import { useIsomorphicLayoutEffect } from '@contra/react-hooks/useIsomorphicLayoutEffect';
import { graphql } from '@contra/relay';
import { type ReactNode, type RefObject, useCallback, useState } from 'react';

type RecommendationsTabTooltipWrapperProps = {
  readonly children: ReactNode;
  readonly flags: readonly UserInterfaceFlag[];
  readonly isRecommendationsTabOnlyVisibleByOwner: boolean;
  readonly positiveRecommendationsOnCurrentProfileCount: number;
  readonly recommendationsTabRef: RefObject<HTMLLIElement>;
  readonly visitorCanEdit: boolean;
};

export const RecommendationsTabTooltipWrapper = ({
  children,
  flags,
  isRecommendationsTabOnlyVisibleByOwner = false,
  positiveRecommendationsOnCurrentProfileCount,
  recommendationsTabRef,
  visitorCanEdit = false,
}: RecommendationsTabTooltipWrapperProps) => {
  const toast = useToast();
  const isMobile = useIsMobile();

  const { selectedUserType } = useSelectedUserType();

  const currentUserPersona =
    selectedUserType === 'CONTRACTOR' ? 'Independent' : 'Client';

  const userFlagToUse =
    selectedUserType === 'CONTRACTOR'
      ? HIDE_CONTRACTOR_FIRST_RECOMMENDATION_TOOLTIP
      : HIDE_CLIENT_FIRST_RECOMMENDATION_TOOLTIP;

  const hasDismissedTooltipOnce = flags.includes(userFlagToUse);

  const [shouldShowRecommendedTooltip, setShouldShowRecommendedTooltip] =
    useState<boolean>(
      positiveRecommendationsOnCurrentProfileCount > 0 &&
        !hasDismissedTooltipOnce,
    );
  const [addFlag] = useContraMutation<RecommendationsTabTooltipWrapperMutation>(
    graphql`
      mutation RecommendationsTabTooltipWrapperMutation(
        $input: AddUserInterfaceFlagInput!
      ) {
        addUserInterfaceFlag(input: $input) {
          errors {
            __typename
            message
          }

          userAccount {
            userInterfaceFlags
          }
        }
      }
    `,
  );

  useIsomorphicLayoutEffect(() => {
    // this is for the case when the user switches view (Client/Independent) and has not dismissed the tooltip
    // if we don't do this, the tooltip will still show on the other view even if they have no recommendations for that particular view
    // since the component does not unmount
    if (hasDismissedTooltipOnce) return;

    if (isMobile && shouldShowRecommendedTooltip) {
      recommendationsTabRef?.current?.scrollIntoView?.();
    }

    setShouldShowRecommendedTooltip(
      positiveRecommendationsOnCurrentProfileCount > 0,
    );
  }, [
    hasDismissedTooltipOnce,
    isMobile,
    positiveRecommendationsOnCurrentProfileCount,
    recommendationsTabRef,
    shouldShowRecommendedTooltip,
  ]);

  const onCompleted = useCallback(
    (errors: ContraMutationErrors) => {
      const { apiErrors, hasErrors } = errors;

      if (hasErrors) {
        const errorMessage =
          apiErrors?.[0].message ?? 'Something went wrong. Please try again.';
        toast.error(errorMessage);
        return;
      }

      setShouldShowRecommendedTooltip(false);
    },
    [toast],
  );

  const onDismissRecommendedTooltip = useCallback(() => {
    addFlag({
      onCompleted,
      onError: () => {
        toast.error('Something went wrong. Please try again');
      },
      variables: {
        input: {
          userInterfaceFlag: userFlagToUse,
        },
      },
    });
  }, [addFlag, onCompleted, toast, userFlagToUse]);

  if (!visitorCanEdit) return <>{children}</>;

  if (isRecommendationsTabOnlyVisibleByOwner) {
    return (
      <Tooltip
        content="Since you don't have any recommendations, this tab is only seen by you"
        hideReferenceContainerOutlineOnFocus
        placement="bottom"
        width={285}
      >
        {children}
      </Tooltip>
    );
  }

  return shouldShowRecommendedTooltip ? (
    <OnboardingTooltip
      arrowPosition={isMobile ? 'top-center' : 'left-center'}
      body={`${
        isMobile ? 'Recommendations' : 'This'
      } tab is now visible to everyone who visits your profile`}
      cta="Got it!"
      heading={`Way to go, you're a recommended ${currentUserPersona}`}
      icon={<ConfettiEmoji />}
      isOpen={shouldShowRecommendedTooltip}
      offset={isMobile ? [0, 16] : [0, 0]}
      onClick={onDismissRecommendedTooltip}
      placement={isMobile ? 'bottom' : 'right'}
      stacking="navbar"
    >
      {children}
    </OnboardingTooltip>
  ) : (
    <Tooltip
      content="This tab is now visible to everyone who visits your profile because you have at least one positive recommendation as Client and/or Independent"
      placement="bottom"
      width={300}
    >
      {children}
    </Tooltip>
  );
};
